import React, { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useDashboardData } from '../hooks/useDashboardData';

/**
 * RecentActivity Component
 * 
 * Displays a list of recent transactions or highest spending transaction
 * Shows transaction date, description, and amount
 * 
 * @param {Array} recentActivity Array of recent transactions
 * @param {boolean} isLoading Loading state
 * @param {boolean} showHighestOnly If true, shows only the highest spending transaction
 * @returns {JSX.Element} RecentActivity component
 */
const RecentActivity = ({ recentActivity, isLoading, showHighestOnly = false, isExpanded = false }) => {
  const [timePeriod, setTimePeriod] = useState('month');
  // Increase default item count when expanded to fill the box better
  const [itemCount, setItemCount] = useState(isExpanded ? 20 : 5);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Update itemCount when isExpanded changes
  useEffect(() => {
    if (isExpanded && itemCount < 20) {
      setItemCount(20);
    } else if (!isExpanded && itemCount > 5) {
      setItemCount(5);
    }
  }, [isExpanded, itemCount]);
  
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between p-2">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                <div className="h-3 w-16 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="h-4 w-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Filter transactions based on time period
  const filterByTimePeriod = (transactions) => {
    if (!transactions || transactions.length === 0) {
      console.log('RecentActivity: No transactions provided to filter');
      return [];
    }
    
    // First, filter out invalid transactions (missing date or zero amount)
    const validTransactions = transactions.filter(tx => {
      if (!tx) return false;
      
      // Must have a date
      if (!tx.date) {
        // Log only first few to avoid spam
        if (transactions.indexOf(tx) < 3) {
          console.log('RecentActivity: Transaction missing date:', { 
            _id: tx._id, 
            description: tx.description, 
            amount: tx.amount,
            type: tx.type 
          });
        }
        return false;
      }
      
      // Must have a non-zero amount (unless it's explicitly an expense type)
      const amount = tx.amount || 0;
      const type = (tx.type || '').toLowerCase();
      
      // Allow expense types even with zero amount (data quality issue)
      if (type === 'expense' || type === 'bills' || type === 'payment') {
        return true;
      }
      
      // Otherwise require non-zero amount
      if (amount === 0) {
        if (transactions.indexOf(tx) < 3) {
          console.log('RecentActivity: Transaction has zero amount:', { 
            _id: tx._id, 
            description: tx.description,
            type: tx.type 
          });
        }
        return false;
      }
      
      return true;
    });
    
    if (validTransactions.length === 0) {
      console.warn('RecentActivity: No valid transactions after filtering invalid data');
      return [];
    }
    
    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today
    let startDate = new Date();
    
    switch (timePeriod) {
      case 'month':
        // Show current month (from start of current month to now)
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'quarter':
        // Show last 3 months (including current month)
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'year':
        // Show last 12 months (including current month)
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        // Default to current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
    }
    
    const filtered = validTransactions.filter(transaction => {
      try {
        const transactionDate = new Date(transaction.date);
        if (isNaN(transactionDate.getTime())) {
          console.error('RecentActivity: Invalid date value:', transaction.date);
          return false;
        }
        
        transactionDate.setHours(0, 0, 0, 0);
        const isValid = transactionDate >= startDate && transactionDate <= now;
        
        return isValid;
      } catch (error) {
        console.error('RecentActivity: Error parsing transaction date:', error, transaction);
        return false;
      }
    });
    
    console.log('RecentActivity: Date filter results:', {
      totalTransactions: transactions.length,
      validTransactions: validTransactions.length,
      filteredCount: filtered.length,
      timePeriod,
      startDate,
      endDate: now
    });
    
    return filtered;
  };

  // Filter and sort transactions - handle empty/null recentActivity
  const filteredTransactions = filterByTimePeriod(recentActivity || []);
  
  // Filter for spending transactions only (EXCLUDE transfers)
  // Expenses can be: negative amounts OR type='expense' OR type='debit' with negative amount
  // OR credit card purchases (type='credit' with positive amount = purchase)
  // EXCLUDE: transfers, payments (positive amounts are likely transfers), bills payments
  const spendingTransactions = filteredTransactions.filter(transaction => {
    const amount = transaction.amount || 0;
    const type = (transaction.type || '').toLowerCase();
    const description = (transaction.description || '').toLowerCase();
    
    // EXCLUDE transfers explicitly
    if (type === 'transfer') {
      return false;
    }
    
    // EXCLUDE payments that are likely transfers (positive amounts)
    // Payments with positive amounts are usually transfers between accounts
    if (type === 'payment' && amount > 0) {
      return false;
    }
    
    // EXCLUDE descriptions that suggest transfers
    const transferKeywords = ['transfer', 'autopay', 'automatic payment', 'payment - thank', 'payment thank'];
    if (transferKeywords.some(keyword => description.includes(keyword))) {
      return false;
    }
    
    // Check if it's an expense:
    // 1. Negative amount (standard expense - debit or credit payment)
    // 2. Type is 'expense' (regardless of amount sign)
    // 3. Type is 'debit' with negative amount
    // 4. Type is 'bills' with negative amount (actual bill payment, not transfer)
    // 5. Type is 'credit' with positive amount (credit card purchase)
    return (amount < 0 && type !== 'transfer') || 
           (type === 'expense' && amount !== 0) || 
           (type === 'bills' && amount < 0) ||
           (type === 'credit' && amount > 0); // Credit card purchases are positive
  });
  
  // Sort by highest spending amounts (absolute value for expenses)
  // For credit card purchases (positive amounts), use the amount directly
  const sortedTransactions = spendingTransactions.sort((a, b) => {
    const aAmount = Math.abs(a.amount || 0);
    const bAmount = Math.abs(b.amount || 0);
    return bAmount - aAmount;
  });
  
  console.log('RecentActivity Debug:', {
    totalTransactions: recentActivity?.length || 0,
    filteredCount: filteredTransactions.length,
    spendingCount: spendingTransactions.length,
    displayCount: sortedTransactions.length,
    sampleTransaction: spendingTransactions[0],
    sampleFiltered: filteredTransactions[0],
    sampleOriginal: recentActivity?.[0],
    timePeriod: timePeriod,
    allTransactions: recentActivity?.slice(0, 5), // Show first 5 for debugging
    spendingTransactionsSample: spendingTransactions.slice(0, 3) // Show first 3 spending transactions
  });

  // Get top N spending transactions (remove duplicates by _id)
  const uniqueTransactions = [];
  const seenIds = new Set();
  for (const tx of sortedTransactions) {
    const id = tx._id || `${tx.description}-${tx.amount}-${tx.date}`;
    if (!seenIds.has(id)) {
      seenIds.add(id);
      uniqueTransactions.push(tx);
    }
  }
  
  const displayActivity = uniqueTransactions.slice(0, itemCount);

  const getIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'income':
      case 'deposit':
        return '💰';
      case 'expense':
      case 'payment':
        return '💳';
      default:
        return '💳';
    }
  };

  const formatAmount = (amount, type) => {
    console.log('Formatting transaction:', { amount, type });
    
    let isPositive = false;
    if (type === 'credit') {
      // Credit card transactions
      // Negative amounts are payments (show as positive/green)
      // Positive amounts are purchases (show as negative/red)
      isPositive = amount < 0;
    } else {
      // Debit transactions
      // Negative amounts are expenses (show as negative/red)
      // Positive amounts are deposits (show as positive/green)
      isPositive = amount > 0;
    }

    const formattedAmount = Math.abs(amount).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });

    console.log('Formatted result:', {
      originalAmount: amount,
      type,
      isPositive,
      display: `${isPositive ? '+' : '-'}${formattedAmount}`
    });

    return (
      <span className={isPositive ? 'text-green-500' : 'text-red-500'}>
        {isPositive ? '+' : '-'}{formattedAmount}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Controls */}
      <div className="flex items-center gap-1 mb-2 sm:mb-4 flex-nowrap overflow-hidden flex-shrink-0">
        {/* Time Period Toggles */}
        <div className="flex bg-gray-100 rounded p-0.5 gap-0.5 flex-shrink-0">
          {['month', 'quarter', 'year'].map((period) => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              className={`px-1 py-[2px] text-[8px] leading-tight rounded transition-colors whitespace-nowrap flex-shrink-0 ${
                timePeriod === period 
                  ? 'bg-white text-gray-800 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Item Count Dropdown */}
        <div className="relative flex-shrink-0 ml-auto">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-0.5 text-[8px] leading-tight text-gray-600 hover:text-gray-800 bg-gray-100 px-1 py-[2px] rounded flex-shrink-0 whitespace-nowrap"
          >
            Top {itemCount}
            <ChevronDownIcon className="h-1.5 w-1.5 flex-shrink-0" />
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              {[5, 10, 15, 20].map((count) => (
                <button
                  key={count}
                  onClick={() => {
                    setItemCount(count);
                    setShowDropdown(false);
                  }}
                  className={`block w-full text-left ${isExpanded ? 'px-3 py-2 text-xs' : 'px-2 py-1.5 text-[9px]'} hover:bg-gray-50`}
                >
                  Top {count}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Transaction List */}
      <div className={`flex-1 min-h-0 overflow-y-auto ${isExpanded ? 'space-y-3' : 'space-y-1.5'}`}>
        {displayActivity.length === 0 ? (
          <div className={`text-gray-500 text-center ${isExpanded ? 'py-4 text-sm sm:text-base' : 'py-2 text-xs sm:text-sm'}`}>
            No spending transactions found for the selected period
          </div>
        ) : (
          displayActivity.map((activity, index) => {
            console.log('Activity:', {
              description: activity.description,
              amount: activity.amount,
              type: activity.type
            });
            
            return (
              <div 
                key={activity._id || index}
                className={`flex items-center justify-between ${isExpanded ? 'py-2 px-1' : 'py-1.5 px-1 sm:py-2 sm:px-2'} hover:bg-gray-50 rounded-lg`}
              >
                <div className="flex items-start gap-2 sm:gap-3 min-w-0">
                  <div className={`${isExpanded ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'} flex-shrink-0`}>
                    {activity.type === 'credit' && activity.amount < 0 ? '💰' : '💳'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-gray-800 ${isExpanded ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} truncate`}>
                      {activity.description || 'Transaction'}
                    </div>
                    <div className={`text-gray-500 ${isExpanded ? 'text-xs sm:text-sm' : 'text-[10px] sm:text-xs'}`}>
                      {formatDate(activity.date)}
                    </div>
                  </div>
                </div>
                <div className={`${isExpanded ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'} font-medium whitespace-nowrap ml-1 sm:ml-2`}>
                  {formatAmount(activity.amount, activity.type)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RecentActivity; 